import { useEffect, useState } from 'react';
import { useIsAuthenticated } from '@azure/msal-react';
import { useAccessToken } from '../auth/use-access-token.js';
import { apiFetch, ApiError, NotSignedIn } from './client.js';

/**
 * Every hour that can actually be booked in a window.
 *
 * There is no filter and none is wanted: the list is unclaimed hours plus
 * groups that still have room, and a closed one to one or a full group is
 * simply absent. What an hour IS gets decided on the way to the cart, so an
 * unclaimed hour reports `seats_free: null` — a size is not knowable until a
 * product is chosen.
 */

export interface Slot {
  slot_id: string;
  slot_start: string;
  slot_end: string;
  /** Null on an unclaimed hour: nobody has decided what it is yet. */
  seats_free: number | null;
  /** The product the first booking claimed it as, or null. */
  claimed_tier_id: string | null;
  coach_id: string;
  coach_name: string;
  facility_id: string;
  facility_name: string;
  resource_id: string;
  /** A clinic (033): its title on the row, its own page, and listed when full. */
  clinic_id: string | null;
  clinic_title: string | null;
}

export type SlotsState =
  | { status: 'loading' }
  | { status: 'ready'; slots: Slot[] }
  | { status: 'failed'; message: string };

export function useSlots(
  from: Date,
  to: Date,
  facilityId: string | null,
  enabled = true,
): { state: SlotsState; reload: () => void } {
  const isAuthenticated = useIsAuthenticated();
  const getToken = useAccessToken();
  const [state, setState] = useState<SlotsState>({ status: 'loading' });
  const [nonce, setNonce] = useState(0);

  // The dates are objects and would be a new instance every render, so the
  // effect keys off their values instead.
  const fromIso = from.toISOString();
  const toIso = to.toISOString();

  useEffect(() => {
    if (!isAuthenticated) return;
    if (!enabled) {
      setState({ status: 'loading' });
      return;
    }

    let cancelled = false;
    setState({ status: 'loading' });

    const query = new URLSearchParams({ from: fromIso, to: toIso });
    if (facilityId) query.set('facility_id', facilityId);

    apiFetch<{ slots: Slot[] }>(`/api/slots?${query.toString()}`, getToken)
      .then((body) => {
        if (!cancelled) setState({ status: 'ready', slots: body.slots });
      })
      .catch((cause: unknown) => {
        if (cancelled) return;
        if (cause instanceof NotSignedIn) return;
        setState({
          status: 'failed',
          message:
            cause instanceof ApiError
              ? cause.message
              : 'The API did not answer. Is the Functions host running on port 7071?',
        });
      });

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, getToken, fromIso, toIso, facilityId, enabled, nonce]);

  return { state, reload: () => setNonce((n) => n + 1) };
}
